"use client";
import React, { useEffect, useState } from "react";
import axiosInstance from "../../../config/axiosConfig";
import { formatMoney } from "../../../utils/utils";
import html2canvas from "html2canvas";

// Mapeo simple de meses
const MONTHS = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
];

const CalendarioDetalleAuxiliar = ({ visible, onClose, periodo, auxiliarID, modo, factorRetencion }) => {
    const [loading, setLoading] = useState(false);
    const [turnos, setTurnos] = useState([]);
    const [adicionales, setAdicionales] = useState([]);
    const [nombreAuxiliar, setNombreAuxiliar] = useState("");

    // Determinar factor de retención si no viene por prop
    const getFactorRetencion = () => {
        if (factorRetencion) return factorRetencion;
        if (!periodo) return 0.855;
        const [anio] = periodo.split("-").map(Number);
        return anio >= 2026 ? 0.8475 : 0.855;
    };

    const factor = getFactorRetencion();

    // Colores para pacientes (reutilizando lógica de DetalleTurnos)
    const colorPool = [
        "#1B73E8", "#DB4437", "#F4B400", "#0F9D58", "#AB47BC",
        "#00ACC1", "#FF7043", "#9E9D24", "#5C6BC0", "#8D6E63"
    ];
    const pacienteColores = new Map();
    let colorIndex = 0;

    const getColorPaciente = (id) => {
        if (!pacienteColores.has(id)) {
            pacienteColores.set(id, colorPool[colorIndex % colorPool.length]);
            colorIndex++;
        }
        return pacienteColores.get(id);
    };

    // Helper fechas
    const parseFechaLocal = (iso) => {
        const [y, m, d] = iso.split("-").map(Number);
        return new Date(y, m - 1, d, 0, 0, 0, 0);
    };

    // ... imports and helpers ...

    // Helper para obtener el día de la semana (0 = Domingo, 1 = Lunes, ..., 6 = Sábado)
    // Ajustaremos para que la semana empiece en Lunes (0 = Lunes, ..., 6 = Domingo)
    const getDiaSemana = (date) => {
        const day = date.getDay();
        // date.getDay() devuelve 0 para Domingo. Queremos 0 para Lunes.
        // Domingo (0) -> 6
        // Lunes (1) -> 0
        return day === 0 ? 6 : day - 1;
    };

    // Generar el array de días del periodo
    const generarCeldasCalendario = () => {
        if (!periodo) return { dias: [], paddingInicio: 0 };
        
        const [anio, mes] = periodo.split("-").map(Number);
        const mesAnterior = mes === 1 ? 12 : mes - 1;
        const anioAnterior = mes === 1 ? anio - 1 : anio;

        const fechaInicio = new Date(anioAnterior, mesAnterior - 1, 26, 0, 0, 0, 0);
        
        // Adelanto: 26 del mes anterior al 10 del mes actual
        // Fin de mes: 26 del mes anterior al 25 del mes actual
        const diaFin = modo === "adelanto" ? 10 : 25;
        const fechaFin = new Date(anio, mes - 1, diaFin, 0, 0, 0, 0);

        const dias = [];
        let current = new Date(fechaInicio);

        while (current <= fechaFin) {
            dias.push(new Date(current));
            current.setDate(current.getDate() + 1);
        }

        const paddingInicio = getDiaSemana(fechaInicio);

        return { dias, paddingInicio };
    };

    // Calcular resumen
    const calcularResumen = () => {
        const { dias } = generarCeldasCalendario();
        if (dias.length === 0) return { totalTurnos: 0, totalValorTurnos: 0, totalAdicionales: 0, totalMov: 0, totalCol: 0, totalGeneral: 0, totalLiquido: 0 };

        const firstDate = dias[0];
        const lastDate = dias[dias.length - 1];

        const formatDate = (date) => {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, "0");
            const d = String(date.getDate()).padStart(2, "0");
            return `${y}-${m}-${d}`;
        };

        const firstDateStr = formatDate(firstDate);
        const lastDateStr = formatDate(lastDate);

        // Filtrar turnos del auxiliar en el rango visible
        const turnosAux = turnos.filter(t => t.fecha >= firstDateStr && t.fecha <= lastDateStr);
        const adicionalesAux = adicionales.filter(a => a.fechaAdicional >= firstDateStr && a.fechaAdicional <= lastDateStr);

        const totalTurnos = turnosAux.length;
        const totalValorTurnos = turnosAux.reduce((acc, t) => acc + (t.valorTurnoRt || 0), 0);
        
        // Sumar adicionales (horas extra, bonos, etc)
        const totalAdicionales = adicionalesAux.reduce((acc, a) => acc + (Number(a.valorTurno) || 0), 0);
        
        // Otros valores (colación, movilización si se quieren sumar al resumen visual)
        const totalMov = turnosAux.reduce((acc, t) => acc + (t.valorLocomocionRt || 0), 0) 
                         + adicionalesAux.reduce((acc, a) => acc + (Number(a.valorMovilizacion) || 0), 0);
        
        const totalCol = turnosAux.reduce((acc, t) => acc + (t.valorColacionRt || 0), 0)
                         + adicionalesAux.reduce((acc, a) => acc + (Number(a.valorColacion) || 0), 0);

        const liquido = (totalValorTurnos + totalAdicionales) * factor + totalMov + totalCol;

        return {
            totalTurnos,
            totalValorTurnos,
            totalAdicionales,
            totalMov,
            totalCol,
            totalGeneral: totalValorTurnos + totalAdicionales + totalMov + totalCol,
            totalLiquido: liquido
        };
    };

    useEffect(() => {
        if (visible && periodo && auxiliarID) {
            cargarData();
        }
    }, [visible, periodo, auxiliarID]);


    const cargarData = async () => {
        try {
            setLoading(true);
            const resp = await axiosInstance.get("/turnos/detalle-auxiliar", {
                params: { periodo, auxiliarID }
            });

            const turnosData = resp.data.turnos?.turnos || [];
            const adicionalesData = resp.data.turnos?.adicionales || [];

            // El backend ya filtra por auxiliarIDDetalle
            setTurnos(turnosData);
            setAdicionales(adicionalesData); 

            // Intentar obtener el nombre del auxiliar de los datos
            if (turnosData.length > 0) {
                setNombreAuxiliar(turnosData[0].auxiliarNombre);
            }

        } catch (error) {
            console.error("Error cargando calendario:", error);
        } finally {
            setLoading(false);
        }
    };
    

    const getTurnoForDate = (dateObj, tipo) => {
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        const isoDate = `${year}-${month}-${day}`;
        return turnos.find(t => t.fecha === isoDate && t.tipo === tipo);
    };

    // Función para copiar al portapapeles
    const copiarCalendario = () => {
        const input = document.getElementById("calendario-capture");
        if (!input) return;

        html2canvas(input, { scale: 2, useCORS: true }).then((canvas) => {
            canvas.toBlob(blob => {
                 if (blob) {
                     const item = new ClipboardItem({ "image/png": blob });
                     navigator.clipboard.write([item]).then(() => {
                         alert("Calendario copiado al portapapeles");
                     }).catch(err => {
                         console.error("Error al copiar:", err);
                         alert("Error al copiar la imagen");
                     });
                 }
            });
        });
    };

    if (!visible) return null;

    const { dias: diasCalendario, paddingInicio } = generarCeldasCalendario();
    const resumen = calcularResumen();
    const headers = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
            <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: "95vw" }}>
                <div className="modal-content">
                    <div className="modal-header bg-primary text-white py-2">
                        <h5 className="modal-title fs-5">
                            Calendario Detalle - {nombreAuxiliar || "Auxiliar"} - {periodo}
                        </h5>
                        <button className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>

                    <div className="modal-body bg-light p-2" id="calendario-capture">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary"></div>
                                <p className="mt-2">Cargando calendario...</p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column" style={{ height: "100%" }}>
                                {/* Headers Semana */}
                                <div className="d-grid text-center fw-bold bg-white border-bottom py-2" 
                                     style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
                                    {headers.map(h => <div key={h}>{h}</div>)}
                                </div>

                                {/* Grid Calendario */}
                                <div className="d-grid" style={{ 
                                    gridTemplateColumns: "repeat(7, 1fr)", 
                                    gap: "4px", 
                                    backgroundColor: "#ddd",
                                    border: "1px solid #ddd"
                                }}>
                                    {/* Celdas vacías iniciales */}
                                    {Array.from({ length: paddingInicio }).map((_, i) => (
                                        <div key={`pad-${i}`} className="bg-white" style={{ minHeight: "100px" }}></div>
                                    ))}

                                    {/* Días */}
                                    {diasCalendario.map((date) => {
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, "0");
                                        const day = String(date.getDate()).padStart(2, "0");
                                        const isoDate = `${year}-${month}-${day}`;
                                        
                                        // Turnos
                                        const turnoDia = getTurnoForDate(date, "day");
                                        const turnoNoche = getTurnoForDate(date, "night");

                                        // Adicionales
                                        const adicDia = adicionales.filter(a => a.fechaAdicional === isoDate && Number(a.tipo_fecha) === 1);
                                        const adicNoche = adicionales.filter(a => a.fechaAdicional === isoDate && (Number(a.tipo_fecha) === 0 || Number(a.tipo_fecha) === 2));

                                        const tieneDia = turnoDia || adicDia.length > 0;
                                        const tieneNoche = turnoNoche || adicNoche.length > 0;

                                        return (
                                            <div key={isoDate} className="bg-white d-flex flex-column" style={{ minHeight: "120px", fontSize: "0.7rem" }}>
                                                {/* Header Día */}
                                                <div className="px-2 py-1 border-bottom text-end bg-light text-secondary fw-bold">
                                                    {day}-{month}-{year}
                                                </div>

                                                {/* Contenido Día */}
                                                <div className="flex-grow-1 p-1 d-flex flex-column gap-1">
                                                    
                                                    {/* Sección Día */}
                                                    <div 
                                                        className={`p-1 rounded mb-1 position-relative`}
                                                        style={{ 
                                                            backgroundColor: tieneDia ? "#e3f2fd" : "transparent",
                                                            border: tieneDia ? "1px solid #90caf9" : "1px dashed #eee",
                                                            minHeight: "50px" 
                                                        }}
                                                    >
                                                        <div className="fw-bold mb-1" style={{ fontSize: "0.65rem", color: tieneDia ? "#0d6efd" : "#ccc" }}>DÍA</div>
                                                        {turnoDia && (
                                                            <>
                                                                <div className="fw-bold text-primary mb-1 lh-1" style={{ fontSize: "0.75rem" }}>
                                                                    {turnoDia.pacienteNombre}
                                                                </div>
                                                                <div className="d-flex flex-wrap gap-1 mb-1">
                                                                    <div className="badge bg-white border border-primary p-1 d-flex align-items-center">
                                                                        <span className="text-primary me-1">T:</span>
                                                                        <span className="text-dark fw-bold" style={{ fontSize: "0.8rem" }}>${formatMoney(turnoDia.valorTurnoRt)}</span>
                                                                    </div>
                                                                    {(turnoDia.valorLocomocionRt > 0) && (
                                                                        <div className="badge bg-white border border-secondary p-1 d-flex align-items-center">
                                                                            <span className="text-secondary me-1">M:</span>
                                                                            <span className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>${formatMoney(turnoDia.valorLocomocionRt)}</span>
                                                                        </div>
                                                                    )}
                                                                    {(turnoDia.valorColacionRt > 0) && (
                                                                        <div className="badge bg-white border border-secondary p-1 d-flex align-items-center">
                                                                            <span className="text-secondary me-1">C:</span>
                                                                            <span className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>${formatMoney(turnoDia.valorColacionRt)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                        {!turnoDia && tieneDia && <div className="text-center text-primary fw-bold">-</div>}
                                                        
                                                        {adicDia.map((a, i) => (
                                                            <div key={`ad-d-${i}`} className="mt-1 pt-1 border-top border-primary border-opacity-25">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-primary fw-bold text-truncate" style={{ fontSize: "0.7rem", maxWidth: "60%" }}>
                                                                        {a.motivoAdicional || "Adic"}
                                                                    </span>
                                                                    <span className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>
                                                                        +${formatMoney(a.valorTurno)}
                                                                    </span>
                                                                </div>
                                                                {(Number(a.valorMovilizacion) > 0 || Number(a.valorColacion) > 0) && (
                                                                    <div className="d-flex gap-2 justify-content-end mt-1">
                                                                        {Number(a.valorMovilizacion) > 0 && (
                                                                            <span className="d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                                                                                <span className="text-secondary me-1">M:</span>
                                                                                <span className="text-dark fw-bold">${formatMoney(a.valorMovilizacion)}</span>
                                                                            </span>
                                                                        )}
                                                                        {Number(a.valorColacion) > 0 && (
                                                                            <span className="d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                                                                                <span className="text-secondary me-1">C:</span>
                                                                                <span className="text-dark fw-bold">${formatMoney(a.valorColacion)}</span>
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>

                                                    {/* Sección Noche */}
                                                    <div 
                                                        className={`p-1 rounded position-relative`}
                                                        style={{ 
                                                            backgroundColor: tieneNoche ? "#ffebee" : "transparent",
                                                            border: tieneNoche ? "1px solid #ef9a9a" : "1px dashed #eee",
                                                            minHeight: "50px" 
                                                        }}
                                                    >
                                                        <div className="fw-bold mb-1" style={{ fontSize: "0.65rem", color: tieneNoche ? "#dc3545" : "#ccc" }}>NOCHE</div>
                                                        {turnoNoche && (
                                                            <>
                                                                <div className="fw-bold text-danger mb-1 lh-1" style={{ fontSize: "0.75rem" }}>
                                                                    {turnoNoche.pacienteNombre}
                                                                </div>
                                                                <div className="d-flex flex-wrap gap-1 mb-1">
                                                                    <div className="badge bg-white border border-danger p-1 d-flex align-items-center">
                                                                        <span className="text-danger me-1">T:</span>
                                                                        <span className="text-dark fw-bold" style={{ fontSize: "0.8rem" }}>${formatMoney(turnoNoche.valorTurnoRt)}</span>
                                                                    </div>
                                                                    {(turnoNoche.valorLocomocionRt > 0) && (
                                                                        <div className="badge bg-white border border-secondary p-1 d-flex align-items-center">
                                                                            <span className="text-secondary me-1">M:</span>
                                                                            <span className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>${formatMoney(turnoNoche.valorLocomocionRt)}</span>
                                                                        </div>
                                                                    )}
                                                                    {(turnoNoche.valorColacionRt > 0) && (
                                                                        <div className="badge bg-white border border-secondary p-1 d-flex align-items-center">
                                                                            <span className="text-secondary me-1">C:</span>
                                                                            <span className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>${formatMoney(turnoNoche.valorColacionRt)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}
                                                        {!turnoNoche && tieneNoche && <div className="text-center text-danger fw-bold">-</div>}

                                                        {adicNoche.map((a, i) => (
                                                            <div key={`ad-n-${i}`} className="mt-1 pt-1 border-top border-danger border-opacity-25">
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span className="text-danger fw-bold text-truncate" style={{ fontSize: "0.7rem", maxWidth: "60%" }}>
                                                                        {a.motivoAdicional || "Adic"}
                                                                    </span>
                                                                    <span className="text-dark fw-bold" style={{ fontSize: "0.75rem" }}>
                                                                        +${formatMoney(a.valorTurno)}
                                                                    </span>
                                                                </div>
                                                                {(Number(a.valorMovilizacion) > 0 || Number(a.valorColacion) > 0) && (
                                                                    <div className="d-flex gap-2 justify-content-end mt-1">
                                                                        {Number(a.valorMovilizacion) > 0 && (
                                                                            <span className="d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                                                                                <span className="text-secondary me-1">M:</span>
                                                                                <span className="text-dark fw-bold">${formatMoney(a.valorMovilizacion)}</span>
                                                                            </span>
                                                                        )}
                                                                        {Number(a.valorColacion) > 0 && (
                                                                            <span className="d-flex align-items-center" style={{ fontSize: "0.75rem" }}>
                                                                                <span className="text-secondary me-1">C:</span>
                                                                                <span className="text-dark fw-bold">${formatMoney(a.valorColacion)}</span>
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>

                                                </div>
                                            </div>
                                        );
                                    })}
                                    
                                    {/* Celdas vacías finales para completar la grilla visualmente si se desea (opcional) */}
                                </div>

                                {/* Resumen Footer */}
                                <div className="mt-3 p-3 bg-white border border-top rounded shadow-sm">
                                    <h6 className="fw-bold border-bottom pb-2 mb-2">Resumen del Periodo Visible</h6>
                                    <div className="row text-center" style={{ fontSize: "0.9rem" }}>
                                        <div className="col border-end">
                                            <div className="text-muted small">Cant. Turnos</div>
                                            <div className="fw-bold fs-5">{resumen.totalTurnos}</div>
                                        </div>
                                        <div className="col border-end">
                                            <div className="text-muted small">Valor Turnos</div>
                                            <div className="fw-bold text-primary">${formatMoney(resumen.totalValorTurnos)}</div>
                                        </div>
                                        <div className="col border-end">
                                            <div className="text-muted small">Adicionales</div>
                                            <div className="fw-bold text-success">${formatMoney(resumen.totalAdicionales)}</div>
                                        </div>
                                        <div className="col border-end">
                                            <div className="text-muted small">Mov + Col</div>
                                            <div className="fw-bold">${formatMoney(resumen.totalMov + resumen.totalCol)}</div>
                                        </div>
                                        <div className="col border-end">
                                             <div className="text-muted small">Total Vista</div>
                                             <div className="fw-bold text-dark fs-5">${formatMoney(resumen.totalGeneral)}</div>
                                         </div>
                                         <div className="col bg-light">
                                             <div className="text-muted small">Total Líquido</div>
                                             <div className="fw-bold text-primary fs-5">${formatMoney(resumen.totalLiquido)}</div>
                                         </div>
                                     </div>
                                 </div>
                            </div>
                        )}
                    </div>
                    <div className="modal-footer py-1 d-flex justify-content-between">
                         <button className="btn btn-sm btn-outline-primary" onClick={copiarCalendario}>
                            📋 Copiar Imagen
                        </button>
                        <button className="btn btn-sm btn-secondary" onClick={onClose}>Cerrar</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CalendarioDetalleAuxiliar;
